import { useState, useEffect } from "react";
import { Link2, Plus, Trash2, Loader2, RefreshCw, Power, QrCode, Copy, Download, ExternalLink, Pencil } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { slug: "", target: "", label: "", active: true };
const inputCls = "w-full bg-[#111] border border-white/15 text-white px-3 py-2.5 text-sm focus:border-[#ff0033] outline-none placeholder:text-neutral-600";

const LinkManager = () => {
  const [items, setItems] = useState([]);
  const [siteUrl, setSiteUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [qr, setQr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/shortlinks");
      setItems(data.items || []); setSiteUrl(data.site_url || "");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.slug.trim()) { toast.error("Kurzname angeben"); return; }
    if (!form.target.trim()) { toast.error("Ziel-URL angeben"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/shortlinks/${form.id}`, form);
      else await api.post("/admin/shortlinks", form);
      toast.success(form.id ? "Kurzlink aktualisiert" : "Kurzlink erstellt");
      setShowForm(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (d) => {
    try { await api.put(`/admin/shortlinks/${d.id}`, { ...d, active: !d.active }); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => {
    const d = delTarget; setDelTarget(null); if (!d) return;
    try { await api.delete(`/admin/shortlinks/${d.id}`); toast.success("Kurzlink gelöscht"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openEdit = (d) => { setForm({ id: d.id, slug: d.slug, target: d.target, label: d.label || "", active: d.active }); setShowForm(true); };
  const copy = async (txt) => { try { await navigator.clipboard.writeText(txt); toast.success("Kopiert"); } catch { /* noop */ } };
  const downloadQr = async (slug) => {
    try {
      const token = localStorage.getItem("gca_token");
      const r = await fetch(`${API}/shortlink/${encodeURIComponent(slug)}/qr`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `QR-${slug}.png`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  return (
    <div data-testid="link-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="font-heading text-2xl uppercase tracking-tight text-white flex items-center gap-3"><Link2 size={22} className="text-[#ff0033]" /> Kurzlinks &amp; QR</h2>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="link-refresh" className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2.5 font-mono-lab text-xs uppercase tracking-widest disabled:opacity-50"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aktualisieren</button>
          <button onClick={() => { setForm(EMPTY); setShowForm(!showForm); }} data-testid="link-new-btn" className="flex items-center gap-2 bg-[#ff0033] text-white px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest hover:bg-[#cc0029] transition-colors"><Plus size={14} /> Kurzlink erstellen</button>
        </div>
      </div>
      <p className="text-neutral-400 text-sm mb-5 max-w-3xl">Eigene, dauerhaft dir gehörende Kurzlinks. Das Ziel kannst du jederzeit ändern – der QR-Code bleibt gleich. Ideal für Poster & Flyer, ohne Abo eines QR-Anbieters.</p>

      {showForm && (
        <div className="border border-white/10 bg-[#0b0b0b] p-4 mb-6 grid md:grid-cols-2 gap-3" data-testid="link-form">
          <label className="block"><span className="text-xs text-neutral-400">Kurzname (a–z, 0–9, Bindestrich)</span>
            <div className="flex items-center gap-1 mt-1"><span className="text-neutral-500 text-sm">{(siteUrl || "").replace(/^https?:\/\//, "")}/r/</span>
              <input data-testid="link-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} className={inputCls} placeholder="handball" /></div></label>
          <label className="block"><span className="text-xs text-neutral-400">Beschreibung (optional)</span><input data-testid="link-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={inputCls} placeholder="z. B. Handball-Aktion Poster" /></label>
          <label className="block md:col-span-2"><span className="text-xs text-neutral-400">Ziel-URL (wohin weitergeleitet wird)</span><input data-testid="link-target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className={inputCls} placeholder="https://golfcoaching-academy.online/code/HANDBALL50" /></label>
          <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#ff0033]" /> Aktiv</label>
          <div className="flex items-end justify-end gap-2">
            <button onClick={() => { setShowForm(false); setForm(EMPTY); }} className="border border-white/15 text-neutral-300 px-4 py-2 font-mono-lab text-xs uppercase">Abbrechen</button>
            <button onClick={save} disabled={saving} data-testid="link-save-btn" className="bg-[#ff0033] text-white px-4 py-2 font-mono-lab text-xs uppercase flex items-center gap-2 disabled:opacity-50">{saving ? <Loader2 size={14} className="animate-spin" /> : null} Speichern</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-neutral-500 py-10 text-center font-mono-lab text-sm">Lädt…</div>
        : items.length === 0 ? <div className="text-neutral-500 py-10 text-center font-mono-lab text-sm" data-testid="link-empty">Noch keine Kurzlinks.</div>
        : (
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest">
                <th className="text-left p-3">Kurzlink</th><th className="text-left p-3">Ziel</th><th className="text-left p-3">Klicks</th><th className="text-left p-3">Status</th><th className="text-right p-3">Aktionen</th>
              </tr></thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-b border-white/5" data-testid={`link-row-${d.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2 text-white font-mono-lab">/r/{d.slug}
                        <button onClick={() => copy(d.short_url)} className="text-neutral-500 hover:text-[#ff0033]" title="Link kopieren"><Copy size={13} /></button></div>
                      {d.label && <div className="text-neutral-500 text-xs mt-0.5">{d.label}</div>}
                    </td>
                    <td className="p-3 text-neutral-400 max-w-[280px] truncate">{d.target}</td>
                    <td className="p-3 text-neutral-400">{d.clicks || 0}</td>
                    <td className="p-3"><span className={`inline-block font-mono-lab text-[10px] uppercase px-2 py-1 border ${d.active ? "text-emerald-400 border-emerald-500/40" : "text-neutral-500 border-white/15"}`}>{d.active ? "Aktiv" : "Inaktiv"}</span></td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setQr(d)} data-testid={`link-qr-${d.id}`} className="inline-flex items-center gap-1 border border-[#ff0033]/50 text-[#ff0033] font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:bg-[#ff0033]/10 transition-colors"><QrCode size={12} /> QR</button>
                        <button onClick={() => toggle(d)} data-testid={`link-toggle-${d.id}`} className="inline-flex items-center gap-1 border border-white/15 text-neutral-300 font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:text-white transition-colors"><Power size={12} /></button>
                        <button onClick={() => openEdit(d)} data-testid={`link-edit-${d.id}`} className="inline-flex items-center gap-1 border border-white/15 text-neutral-300 font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:text-white transition-colors"><Pencil size={12} /></button>
                        <button onClick={() => setDelTarget(d)} data-testid={`link-delete-${d.id}`} className="inline-flex items-center gap-1 border border-white/15 text-neutral-400 font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:text-[#ff0033] transition-colors"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {qr && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="link-qr-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setQr(null)} />
          <div className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-sm p-6 text-center">
            <div className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase">Dynamischer QR-Code</div>
            <div className="font-heading text-xl text-white tracking-widest mt-1 mb-4">{qr.slug === "001praxis" ? "PRAXIS" : `/r/${qr.slug}`}</div>
            <div className="bg-white p-3 inline-block mb-4">
              <img src={`${API}/shortlink/${encodeURIComponent(qr.slug)}/qr`} alt={`QR ${qr.slug}`} className="w-56 h-56" data-testid="link-qr-image" />
            </div>
            <div className="flex items-center gap-2 bg-[#111] border border-white/10 px-3 py-2 mb-2">
              <ExternalLink size={13} className="text-[#ff0033] shrink-0" /><span className="text-neutral-300 text-xs break-all text-left flex-1">{qr.short_url}</span>
            </div>
            <div className="text-neutral-500 text-[11px] mb-4">→ leitet aktuell auf: <span className="text-neutral-300 break-all">{qr.target}</span></div>
            <div className="flex gap-2">
              <button onClick={() => downloadQr(qr.slug)} data-testid="link-qr-download" className="flex-1 flex items-center justify-center gap-2 bg-[#ff0033] text-white font-mono-lab text-[11px] uppercase tracking-widest py-2.5 hover:bg-[#cc0029] transition-colors"><Download size={14} /> PNG</button>
              <button onClick={() => copy(qr.short_url)} data-testid="link-qr-copy" className="flex-1 flex items-center justify-center gap-2 border border-white/15 text-neutral-300 font-mono-lab text-[11px] uppercase tracking-widest py-2.5 hover:border-[#ff0033] hover:text-white transition-colors"><Copy size={14} /> Link</button>
            </div>
            <button onClick={() => setQr(null)} className="mt-4 text-neutral-500 text-xs hover:text-white">Schließen</button>
          </div>
        </div>
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="link-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Kurzlink löschen?</AlertDialogTitle><AlertDialogDescription>{delTarget && `/r/${delTarget.slug} wird dauerhaft entfernt. Bereits gedruckte QR-Codes würden dann ins Leere führen.`}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-[#ff0033] hover:bg-[#cc0029]">Löschen</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LinkManager;
